<div class="profile-page page-container">
  <div class="form-container">
    <!-- Header -->
    <div class="page-header">
      <h1 class="page-title">Mi Perfil</h1>
      <p class="page-subtitle">Gestiona tu información personal y configuración de la cuenta.</p>
    </div>

    @if (contractStatusService.hasPendingSignature()) {
      <a routerLink="/mi-ficha" class="alert-info mb-6">
        <fa-icon [icon]="faCircleInfo"></fa-icon>
        <p>
          Tienes un contrato pendiente de firma. Por favor,
          <span class="underline font-semibold">revisa tu contrato en Mi Ficha</span>
          para firmarlo.
        </p>
      </a>
    }

    <div class="profile-form mb-6">
      <div class="form-section !mb-0 !p-4">
        <div class="flex items-start gap-4">
          <div class="flex-1 pt-0.5 text-sm text-gray-600 dark:text-gray-300">
            <p class="leading-relaxed">
              <fa-icon
                [icon]="faLock"
                class="mx-1 text-xs text-gray-400 dark:text-gray-500"
              ></fa-icon>
              Estos campos son de solo lectura y reflejan la información registrada en nuestro
              sistema.
            </p>
            <div
              class="mt-3 flex items-center gap-1.5 text-[13px] text-gray-500 dark:text-gray-400"
            >
              <span>¿Necesitas actualizar estos datos?</span>
              <a
                routerLink="/contact"
                class="font-bold text-brand-red underline hover:opacity-80 transition-opacity"
              >
                Contacta con SDi
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    @if (isProfileLoading) {
      <div class="profile-form mb-6">
        <div
          class="form-section !mb-0 !p-12 flex flex-col items-center justify-center min-h-[150px]"
        >
          <div class="app-spinner mb-4 border-brand-red"></div>
          <p class="text-sm text-gray-500 animate-pulse font-medium">Cargando perfil...</p>
        </div>
      </div>
    } @else {
      @if (error) {
        <div class="error-message">
          {{ error }}
        </div>
      }

      <div class="profile-form">
        <!-- Contact Person Section -->
        <div class="form-section">
          <div class="section-header with-action">
            <div class="flex items-center gap-3">
              <fa-icon [icon]="faUser" class="section-icon"></fa-icon>
              <h2 class="section-title">Datos Personales</h2>
            </div>
            <button type="button" (click)="openPasswordModal()" class="btn btn-secondary btn-sm">
              <fa-icon [icon]="faLock" class="mr-2"></fa-icon>
              Cambiar Contraseña
            </button>
          </div>

          <div class="form-group">
            <label for="name">Nombre completo</label>
            <input
              type="text"
              id="name"
              class="app-input readonly-field"
              readonly
              [value]="profileForm.get('name')?.value"
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="email">Email</label>
              <input
                type="email"
                id="email"
                class="app-input readonly-field"
                readonly
                [value]="profileForm.get('email')?.value"
              />
            </div>

            <div class="form-group">
              <label for="phone">Número de Teléfono</label>
              <input
                type="tel"
                id="phone"
                class="app-input readonly-field"
                readonly
                [value]="profileForm.get('phone')?.value"
              />
            </div>
          </div>

        </div>

        <!-- Appearance Preferences Section -->
        <div class="form-section">
          <div class="section-header">
            <fa-icon [icon]="faDesktop" class="section-icon"></fa-icon>
            <h2 class="section-title">Preferencias de Apariencia</h2>
          </div>
          
          <p class="text-sm text-gray-500 mb-4">
            Elige cómo quieres que se vea la aplicación.
          </p>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <!-- System Theme -->
            <button 
              type="button"
              (click)="setTheme('system')"
              class="flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all"
              [class.border-brand-red]="themeService.theme() === 'system'"
              [class.bg-brand-red/5]="themeService.theme() === 'system'"
              [class.border-gray-100]="themeService.theme() !== 'system'"
              [class.dark:border-gray-700]="themeService.theme() !== 'system'"
            >
              <fa-icon [icon]="faDesktop" class="text-xl" [class.text-brand-red]="themeService.theme() === 'system'"></fa-icon>
              <div class="text-center">
                <p class="text-sm font-semibold dark:text-white">Sistema</p>
                <p class="text-[10px] text-gray-500">Sigue la configuración de tu dispositivo</p>
              </div>
            </button>

            <!-- Light Theme -->
            <button 
              type="button"
              (click)="setTheme('light')"
              class="flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all"
              [class.border-brand-red]="themeService.theme() === 'light'"
              [class.bg-brand-red/5]="themeService.theme() === 'light'"
              [class.border-gray-100]="themeService.theme() !== 'light'"
              [class.dark:border-gray-700]="themeService.theme() !== 'light'"
            >
              <fa-icon [icon]="faSun" class="text-xl" [class.text-brand-red]="themeService.theme() === 'light'"></fa-icon>
              <div class="text-center">
                <p class="text-sm font-semibold dark:text-white">Claro</p>
                <p class="text-[10px] text-gray-500">Apariencia clara siempre</p>
              </div>
            </button>

            <!-- Dark Theme -->
            <button 
              type="button"
              (click)="setTheme('dark')"
              class="flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all"
              [class.border-brand-red]="themeService.theme() === 'dark'"
              [class.bg-brand-red/5]="themeService.theme() === 'dark'"
              [class.border-gray-100]="themeService.theme() !== 'dark'"
              [class.dark:border-gray-700]="themeService.theme() !== 'dark'"
            >
              <fa-icon [icon]="faMoon" class="text-xl" [class.text-brand-red]="themeService.theme() === 'dark'"></fa-icon>
              <div class="text-center">
                <p class="text-sm font-semibold dark:text-white">Oscuro</p>
                <p class="text-[10px] text-gray-500">Apariencia oscura siempre</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    }
  </div>
</div>

<!-- Change Password Modal -->
@if (showPasswordModal) {
  <div class="modal-backdrop">
    <div class="modal-content">
      <div class="modal-header">
        <h3 id="modal-title" class="modal-title">Cambiar Contraseña</h3>
        <button
          type="button"
          (click)="closePasswordModal()"
          class="close-button"
          aria-label="Cerrar modal"
        >
          <fa-icon [icon]="faXmark"></fa-icon>
        </button>
      </div>

      <form [formGroup]="passwordForm" (ngSubmit)="onChangePassword()">
        <div class="modal-body">
          <div class="form-group">
            <label for="currentPassword">Contraseña Actual</label>
            <div class="password-input-wrapper">
              <input
                [type]="showCurrentPassword ? 'text' : 'password'"
                id="currentPassword"
                class="app-input"
                placeholder="••••••••"
                formControlName="currentPassword"
                [class.invalid]="fp['currentPassword'].invalid && fp['currentPassword'].touched"
              />
              <button
                type="button"
                class="password-toggle"
                (click)="toggleCurrentPassword()"
                aria-label="Toggle password visibility"
              >
                <fa-icon [icon]="showCurrentPassword ? faEyeSlash : faEye"></fa-icon>
              </button>
            </div>
            @if (fp['currentPassword'].invalid && fp['currentPassword'].touched) {
              <div class="error-text">
                @if (fp['currentPassword'].errors?.['required']) {
                  <span>La contraseña actual es obligatoria</span>
                }
              </div>
            }
          </div>

          <div class="form-group">
            <label for="newPassword">Nueva Contraseña</label>
            <div class="password-input-wrapper">
              <input
                [type]="showNewPassword ? 'text' : 'password'"
                id="newPassword"
                class="app-input"
                placeholder="••••••••"
                formControlName="newPassword"
                [class.invalid]="fp['newPassword'].invalid && fp['newPassword'].touched"
              />
              <button
                type="button"
                class="password-toggle"
                (click)="toggleNewPassword()"
                aria-label="Toggle password visibility"
              >
                <fa-icon [icon]="showNewPassword ? faEyeSlash : faEye"></fa-icon>
              </button>
            </div>
            @if (fp['newPassword'].invalid && fp['newPassword'].touched) {
              <div class="error-text">
                @if (fp['newPassword'].errors?.['required']) {
                  <span>La nueva contraseña es obligatoria</span>
                }
                @if (fp['newPassword'].errors?.['minlength']) {
                  <span>La contraseña debe tener al menos 8 caracteres</span>
                }
              </div>
            }
          </div>

          <div class="form-group">
            <label for="confirmPassword">Confirmar Contraseña</label>
            <div class="password-input-wrapper">
              <input
                [type]="showConfirmPassword ? 'text' : 'password'"
                id="confirmPassword"
                class="app-input"
                placeholder="••••••••"
                formControlName="confirmPassword"
                [class.invalid]="fp['confirmPassword'].invalid && fp['confirmPassword'].touched"
              />
              <button
                type="button"
                class="password-toggle"
                (click)="toggleConfirmPassword()"
                aria-label="Toggle password visibility"
              >
                <fa-icon [icon]="showConfirmPassword ? faEyeSlash : faEye"></fa-icon>
              </button>
            </div>
            @if (fp['confirmPassword'].invalid && fp['confirmPassword'].touched) {
              <div class="error-text">
                @if (fp['confirmPassword'].errors?.['required']) {
                  <span>Debes confirmar la contraseña</span>
                }
              </div>
            }
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            (click)="closePasswordModal()"
            class="app-btn app-btn--secondary"
            [disabled]="isLoading"
          >
            Cancelar
          </button>
          <button type="submit" class="app-btn app-btn--primary" [disabled]="isLoading">
            {{ isLoading ? 'Cambiando...' : 'Cambiar Contraseña' }}
          </button>
        </div>
      </form>
    </div>
  </div>
}
